import React, { useEffect, useState } from 'react';
import { Alert, Form, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import {
  loadPZCorrectionReceiptDatePreview,
  PZCorrectionReceiptDatePreview as PreviewDto,
} from './warehouseCorrectionApi';

/**
 * Owner decision 2026-09-18: the PZ receipt date is the exchange-rate date of
 * its batch. When the operator moves `receiptDate` in a correction of a
 * received PZ, this panel asks the backend what that means before the save:
 * the NBP rate the new date carries and the customer orders (numbers only)
 * whose issued cost will be recalculated. Orders with a recognised settlement
 * are named up front, because the save would be refused for them.
 *
 * Lives inside the PZDetail antd Form (reads `receiptDate` via Form.useWatch);
 * it never computes a rate or a cost itself.
 */
const rateFormatter = new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const formatDay = (value: string | null) => (value ? dayjs(value).format('DD.MM.YYYY') : '—');

export default function PZCorrectionReceiptDatePreview({
  documentId,
  currentReceiptDate,
  currency,
}: {
  documentId: string;
  currentReceiptDate: string;
  currency: string;
}) {
  const { t } = useTranslation();
  const watched = Form.useWatch<Dayjs | undefined>('receiptDate');
  const nextDate = watched && dayjs.isDayjs(watched) && watched.isValid() ? watched.format('YYYY-MM-DD') : null;
  const currentDate = currentReceiptDate ? dayjs(currentReceiptDate).format('YYYY-MM-DD') : null;
  const changed = Boolean(nextDate && currentDate && nextDate !== currentDate);
  const [preview, setPreview] = useState<PreviewDto | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');

  useEffect(() => {
    if (!changed || !nextDate) {
      setPreview(null);
      setState('idle');
      return undefined;
    }
    let active = true;
    setState('loading');
    const timer = window.setTimeout(() => {
      loadPZCorrectionReceiptDatePreview(documentId, nextDate)
        .then((result) => {
          if (!active) return;
          setPreview(result);
          setState('idle');
        })
        .catch(() => {
          if (!active) return;
          setPreview(null);
          setState('failed');
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [changed, nextDate, documentId]);

  if (!changed) return null;

  const affected = preview?.affectedOrders || [];
  const settled = affected.filter((order) => order.settled);
  const orderList = (rows: typeof affected) => rows.map((order) => order.orderNumber).join(', ');
  const rate = preview?.exchangeRate.rate;

  return (
    <Alert
      type={settled.length ? 'error' : 'warning'}
      showIcon
      data-testid="pz-correction-receipt-date-preview"
      message={t('pz:correction.receiptDatePreview.title')}
      description={(
        <div>
          <div>{t('pz:correction.receiptDatePreview.description')}</div>
          {state === 'loading' && (
            <div><Spin size="small" /> {t('pz:correction.receiptDatePreview.loading')}</div>
          )}
          {state === 'failed' && <div>{t('pz:correction.receiptDatePreview.failed')}</div>}
          {preview && (
            <>
              {currency === 'PLN' || preview.currency === 'PLN' ? (
                <div>{t('pz:correction.receiptDatePreview.ratePLN')}</div>
              ) : (
                <div>
                  <strong>
                    {t('pz:correction.receiptDatePreview.rate', {
                      currency: preview.currency,
                      rate: rate != null ? rateFormatter.format(rate) : '—',
                    })}
                  </strong>
                  {preview.exchangeRate.effectiveDate && (
                    <>
                      {' '}
                      <span>
                        (
                        {t('pz:correction.receiptDatePreview.rateSource', {
                          effectiveDate: formatDay(preview.exchangeRate.effectiveDate),
                          tableNumber: preview.exchangeRate.tableNumber || '—',
                        })}
                        )
                      </span>
                    </>
                  )}
                  {preview.currentExchangeRateToPLN != null && (
                    <>
                      <br />
                      {t('pz:correction.receiptDatePreview.previousRate', {
                        rate: rateFormatter.format(preview.currentExchangeRateToPLN),
                        date: formatDay(preview.currentReceiptDate),
                      })}
                    </>
                  )}
                </div>
              )}
              <div>
                {affected.length
                  ? t('pz:correction.receiptDatePreview.affectedOrders', { orders: orderList(affected) })
                  : t('pz:correction.receiptDatePreview.noAffectedOrders')}
              </div>
              {settled.length > 0 && (
                <div><strong>{t('pz:correction.receiptDatePreview.settledOrders', { orders: orderList(settled) })}</strong></div>
              )}
            </>
          )}
        </div>
      )}
    />
  );
}
